import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { supabaseAdmin } from "@/integrations/supabase/client.server";

async function assertAdmin(userId: string) {
  const { data, error } = await supabaseAdmin
    .from("user_roles")
    .select("id")
    .eq("user_id", userId)
    .eq("role", "admin")
    .maybeSingle();
  if (error) throw new Error(error.message);
  if (!data) throw new Error("Forbidden");
}

/**
 * Called from the client on SIGNED_IN. Returns whether the caller should
 * trigger sending the welcome-signup email (i.e. has confirmed email and
 * we have never marked them as sent before). Marks them atomically.
 */
export const claimWelcomeEmailSend = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { userId } = context;
    const { data: userRes, error: userErr } =
      await supabaseAdmin.auth.admin.getUserById(userId);
    if (userErr || !userRes?.user) return { shouldSend: false };
    const user = userRes.user;
    if (!user.email || !user.email_confirmed_at) return { shouldSend: false };

    // Atomic claim: insert; on conflict do nothing.
    const { data: inserted, error: insErr } = await supabaseAdmin
      .from("welcome_emails_sent")
      .insert({ user_id: userId, email: user.email })
      .select("user_id")
      .maybeSingle();
    if (insErr) {
      // Conflict means somebody else already claimed it.
      if ((insErr as any).code === "23505") return { shouldSend: false };
      throw new Error(insErr.message);
    }
    if (!inserted) return { shouldSend: false };

    return {
      shouldSend: true,
      email: user.email,
      name:
        (user.user_metadata as any)?.full_name ||
        (user.user_metadata as any)?.name ||
        null,
    };
  });

/** Admin-only: returns confirmed users that never got the welcome email and
 * marks them as sent. The caller then triggers sending one by one. */
export const claimWelcomeBackfill = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertAdmin(context.userId);

    const { data: usersList, error } =
      await supabaseAdmin.auth.admin.listUsers({ perPage: 1000 });
    if (error) throw new Error(error.message);

    const { data: already, error: aErr } = await supabaseAdmin
      .from("welcome_emails_sent")
      .select("user_id");
    if (aErr) throw new Error(aErr.message);
    const sentIds = new Set((already ?? []).map((r) => r.user_id));

    const targets = (usersList.users ?? []).filter(
      (u) => u.email && u.email_confirmed_at && !sentIds.has(u.id),
    );

    if (targets.length === 0) return { recipients: [] as Array<{ email: string; name: string | null }> };

    // Mark them all sent (atomic claim) BEFORE returning so a retry doesn't double-send.
    const rows = targets.map((u) => ({ user_id: u.id, email: u.email! }));
    const { error: insErr } = await supabaseAdmin
      .from("welcome_emails_sent")
      .insert(rows);
    if (insErr) throw new Error(insErr.message);

    return {
      recipients: targets.map((u) => ({
        email: u.email!,
        name:
          (u.user_metadata as any)?.full_name ||
          (u.user_metadata as any)?.name ||
          null,
      })),
    };
  });
